import React, { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import styled from 'styled-components/macro';
import { useHistory, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import * as firebase from 'firebase/app';
import 'firebase/auth';
import { useAuth } from 'shared/providers/AuthProvider';
import useClinicUser from 'shared/hooks/useClinicUser';
import { email as emailRegex } from 'shared/utils/validation';
import { UserType as UserType } from 'shared/types/graphql';
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
} from '@televet/kibble-ui/build/components/Modal';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { TextInput } from '@televet/kibble-ui/build/components/TextInput';
import { Alert } from '@televet/kibble-ui/build/components/Alert';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Link, VStack } from '@televet/kibble-ui';
import { useTracking } from 'shared/providers/TrackingProvider';
import { SettingsMyProfileEvents } from 'shared/providers/TrackingProvider/EventNames';

interface IChangeEmailForm {
  currentEmail: string;
  currentPassword: string;
  newEmail?: string;
  newEmailConfirmation?: string;
}

interface IChangeEmailModalProps {
  updateUser: Function; // eslint-disable-line @typescript-eslint/ban-types
  isOpen: boolean;
  onClose: () => void;
}

const ChangeEmailModal = ({ updateUser, isOpen, onClose }: IChangeEmailModalProps): JSX.Element => {
  const { user, logout } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const { isImpersonating } = useAuth();
  const [isRecentlyAuthenticated, setIsRecentlyAuthenticated] = useState(isImpersonating);
  const history = useHistory();
  const location = useLocation();
  const { clinicUser } = useClinicUser();
  const clinicUserId = clinicUser?.id;
  const toast = useToast();
  const { trackEvent } = useTracking();

  const { handleSubmit, register, watch, triggerValidation, setError, formState, errors } = useForm<IChangeEmailForm>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    validateCriteriaMode: 'all',
  });

  const newEmail = watch('newEmail');
  const newEmailConfirmation = watch('newEmailConfirmation');

  const reauthenticateUser = async (currentEmail: string, currentPassword: string): Promise<void> => {
    setIsLoading(true);
    try {
      await user?.reauthenticateWithCredential(
        firebase.auth.EmailAuthProvider.credential(currentEmail, currentPassword),
      );
      setIsRecentlyAuthenticated(true);
    } catch (error) {
      Sentry.captureException(error);
      setError(
        'currentPassword',
        'manual',
        error?.message || 'Whoops! Something went wrong confirming your password. Please try again.',
      );
    } finally {
      setIsLoading(false);
    }
  };

  const updateUserEmail = async (newEmail: string): Promise<void> => {
    trackEvent(SettingsMyProfileEvents.EditUserEmailConfirmed);
    setIsLoading(true);
    try {
      await user?.updateEmail(newEmail);
      await updateUser({ variables: { where: { id: clinicUserId }, data: { email: newEmail, type: UserType.Vet } } });
      onClose();
      toast({
        status: 'success',
        description: `Success! Your Otto email address is now ${newEmail}.`,
      });
    } catch (error) {
      Sentry.captureException(error);

      if (error?.code === 'auth/requires-recent-login') {
        setIsRecentlyAuthenticated(false);
        setError('currentPassword', {
          type: 'manual',
          message: 'Please provide your current password to continue.',
        });
      } else {
        const isEmailInUse =
          error?.code === 'auth/email-already-in-use' ||
          error?.message.includes('A unique constraint would be violated on User');
        const errorMessage = isEmailInUse
          ? 'Whoops! It looks like that email address is already in use. Please provide a different one to continue.'
          : error?.message || 'Whoops! There was an error updating your email address. Please try again.';
        setError('newEmail', 'manual', errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onFormSubmit = async ({ currentPassword, newEmail }: IChangeEmailForm): Promise<void> => {
    if (!clinicUser?.email) return;
    if (!isRecentlyAuthenticated) {
      reauthenticateUser(clinicUser.email, currentPassword);
    } else if (newEmail) {
      updateUserEmail(newEmail);
    }
  };

  const onForgotPasswordClick = (): void => {
    const params = new URLSearchParams(location.search);
    params.set('forgotPassword', clinicUser?.email || 'true');
    history.replace(`${window.location.pathname}?${params.toString()}`);
    logout({ keepLocalData: true });
  };

  /**
   * Trigger email confirmation validation to show alert before user submits form
   */
  useEffect(() => {
    if (newEmail && newEmail === newEmailConfirmation) triggerValidation('newEmailConfirmation');
  }, [newEmail, newEmailConfirmation, triggerValidation]);

  return (
    <Modal size="md" isOpen={isOpen} onClose={onClose}>
      <ModalHeader>Change Email Address</ModalHeader>
      <ModalCloseButton />
      <Form onSubmit={handleSubmit(onFormSubmit)}>
        <ModalBody>
          <VStack>
            <Text variant="subtle">
              {!isRecentlyAuthenticated &&
                'To get started changing your Otto email address, please confirm your current password below.'}
            </Text>
            <TextInput
              name="currentEmail"
              label="Current Email"
              isDisabled
              labelStyle={{ fontWeight: 'semibold' }}
              value={clinicUser?.email || ''}
              onChange={(): null => null}
              ref={register({ required: true })}
            />
            <Text>
              {isRecentlyAuthenticated && 'Looks good! To continue, provide and confirm a new email address below.'}
            </Text>
            {!isRecentlyAuthenticated ? (
              <TextInput
                name="currentPassword"
                label="Current Password"
                type="password"
                autoFocus={true}
                labelStyle={{ fontWeight: 'semibold' }}
                errorText={`${errors.currentPassword?.message}`}
                isInvalid={!!errors.currentPassword}
                ref={register({
                  required: {
                    value: true,
                    message: 'Please provide your current password to continue.',
                  },
                })}
              />
            ) : (
              <>
                <TextInput
                  name="newEmail"
                  label="New Email"
                  autoFocus={true}
                  ref={register({
                    validate: {
                      isNewEmail: (value): boolean | string =>
                        (value && value !== clinicUser?.email) || 'Please provide a new email address.',
                      isValid: (value): boolean | string => {
                        return emailRegex.test(value) || 'Please provide a valid email address (e.g. brady@otto.com)';
                      },
                    },
                  })}
                  errorText={`${errors.newEmail?.message}`}
                  isInvalid={!!errors.newEmail}
                  onBlur={(): void => {
                    trackEvent(SettingsMyProfileEvents.EnterNewuserEmail);
                  }}
                />
                <TextInput
                  name="newEmailConfirmation"
                  label="Confirm New Email"
                  ref={register({
                    validate: (value): boolean | string => {
                      return (
                        (value && value === newEmail) ||
                        "It looks like those don't match. Please enter the same email address you provided above."
                      );
                    },
                  })}
                  errorText={`${errors.newEmailConfirmation?.message}`}
                  isInvalid={!!errors.newEmailConfirmation}
                  onBlur={(): void => {
                    trackEvent(SettingsMyProfileEvents.ConfirmNewUserEmail);
                  }}
                />
              </>
            )}
            {!isRecentlyAuthenticated && (
              <Text size="sm" variant="subtle">
                Forgot your password? You can{' '}
                <Link size="sm" onClick={onForgotPasswordClick}>
                  log out and reset your password
                </Link>{' '}
                using your current email address.
              </Text>
            )}
            {isRecentlyAuthenticated && formState.isValid && (
              <Alert
                status="warning"
                hideCloseButton
                title="Heads up!"
                description={
                  <Text>
                    Once you change your Otto email address, you&apos;ll stop receiving notifications at{' '}
                    <Text fontWeight="semibold">{clinicUser?.email || 'your previous email address'}</Text> and will
                    only be able to log in using <Text fontWeight="semibold">{newEmail}</Text>
                  </Text>
                }
              />
            )}
          </VStack>
        </ModalBody>
        <ModalFooter justifyContent="space-between">
          <Button
            variant="tertiary"
            type="button"
            onClick={(): void => {
              trackEvent(SettingsMyProfileEvents.CancelEditUserEmail);
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button isLoading={isLoading} type="submit">
            {!isRecentlyAuthenticated ? 'Confirm Password' : 'Save'}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default ChangeEmailModal;

const Form = styled.form``;
